import { useMemo } from "react";
import { useDeterministicReport } from "hooks/useDeterministicReport";
import { useSessionFocus } from "hooks/useSessionFocus";
import { useStudentCollabRollups } from "hooks/useStudentCollabRollups";
import { pillsByName } from "hooks/useSoloAccuracy";
import { surveyByName } from "hooks/useSessionSurvey";
import { useTaggedQuotes } from "hooks/useTaggedQuotes";
import { warmUpLabel } from "utils/warmUpLabel";

const EMPTY_ROWS = [];

/**
 * Re-key a rollup by the name the report calls each student.
 *
 * The rollup arrives keyed both ways. Reading `byId` through the report's own
 * id -> name map produces a lookup the renderers can do with the name they are
 * already rendering, without either side having to spell it the same way.
 *
 * Falls back to the rollup's own name keys when a student is not in the map —
 * an educator in the participants, or a report carrying no `_participant_ids`
 * because it was generated before this shipped.
 */
const resolveByReportName = (rollups, nameById) => {
  const byReportName = { ...rollups.byName };
  Object.entries(rollups.byId || {}).forEach(([id, level]) => {
    const name = nameById[id];
    if (name) byReportName[String(name).toLowerCase()] = level;
  });
  return byReportName;
};

/**
 * Everything the session report shows from the backend rather than from the LLM.
 *
 * ONE builder, for every surface that renders the report. It lived inside
 * `Summary` and was copied into the admin prompt console, which is how the two
 * came to disagree about which educator to read for and whether to reconstruct
 * missing collaboration scores. A second copy of these keys is the drift
 * this file exists to prevent: `Summary` calls it, and so does any surface that
 * renders `ParsedSummary` directly.
 *
 * @param {object} params
 * @param {object|null} params.value The session row. Its `started_at` opens the
 *   cutoff gate, its `session_id` keys every backend read.
 * @param {Object<string, Array>|undefined} params.challengeData The challenge
 *   rows keyed by session id.
 * @param {Object<string, string>|null} params.participantIds The report's own
 *   `_participant_ids`: the name each student is called in THIS report, mapped
 *   to their user id. Everything below is keyed by id, so this is what joins
 *   the two without comparing name strings that come from different systems —
 *   the report's names are Firebase display names, the scores carry the
 *   Postgres first + last. Absent, the join falls back to matching on the name,
 *   which is what it did before and what silently dropped a student's figures
 *   whenever the two spellings differed.
 * @param {boolean} params.forceDeterministic Open the gate regardless of the
 *   logged-in user's rollout flag. For surfaces whose reader is not the
 *   session's educator; absent, the gate alone decides.
 * @param {string|null} params.educatorId Scope every read to the educator who
 *   ran the session. Absent, each endpoint answers for the caller, which is
 *   what a teacher reading their own report wants.
 * @param {boolean} params.reconstructCollabLevels Ask for collaboration scores
 *   to be recomputed for a session that never scored them.
 * @returns {object} The `deterministic` prop `ParsedSummary` expects.
 */
export const useReportDeterministic = ({
  value,
  challengeData,
  participantIds = null,
  forceDeterministic = false,
  educatorId = null,
  reconstructCollabLevels = false,
}) => {
  // The gate is a teacher-facing rollout control keyed to the LOGGED-IN user, so
  // it has no business standing between an admin and the data. Absent, the gate
  // alone decides and the teacher's path is exactly as it was.
  const enabled =
    useDeterministicReport(value?.started_at) || Boolean(forceDeterministic);
  const focus = useSessionFocus(enabled ? value : null);
  // Absent, both extra arguments are the defaults the hooks already have, so a
  // teacher's report asks for exactly the URL it asked for before.
  const collabRollups = useStudentCollabRollups(
    value?.session_id,
    enabled,
    educatorId,
    reconstructCollabLevels,
  );

  const challenges = challengeData?.[value?.session_id] || EMPTY_ROWS;

  // The challenge rows already carry each student's id alongside their resolved
  // name; the solo-accuracy endpoint keys by id alone, so this is what joins
  // the two.
  const studentNameById = useMemo(() => {
    const byId = {};
    challenges.forEach((challenge) => {
      (challenge?.submissions || []).forEach(({ student_id: id, student_name: name }) => {
        if (id && name) byId[id] = name;
      });
    });
    return byId;
  }, [challenges]);

  // Read from the row rather than asked for per session. While the pills on the
  // collapsed card fetched it too, this was a cache hit; once they stopped, it
  // would have become a fresh request every time a card was opened.
  // The report's own names win: they are the strings the bullets are labelled
  // with, so keying the figures by them makes the lookup exact rather than a
  // string comparison across two name sources.
  const nameById = useMemo(() => {
    if (!participantIds) return studentNameById;

    const byId = { ...studentNameById };
    Object.entries(participantIds).forEach(([name, id]) => {
      if (id && name) byId[id] = name;
    });
    return byId;
  }, [participantIds, studentNameById]);

  const soloAccuracy = pillsByName(value?.solo_accuracy, nameById);
  // Same row, same join: the survey answers key by student id too.
  const endOfSessionSurvey = surveyByName(
    value?.end_of_session_survey,
    studentNameById,
  );
  // Not behind the gate: the tagger's flags are safety data, not report
  // numbers — a teacher outside the deterministic rollout is still told about
  // a flagged quote.
  const taggedQuotes = useTaggedQuotes(
    value?.session_id,
    Boolean(value?.session_id),
    educatorId,
  );

  return {
    enabled,
    groupCollabLevel: enabled ? value?.group_collab_level : undefined,
    // A warm-up's set name is a fact about the session, not a report figure, so
    // it is the one value that does not wait for the gate.
    focus: warmUpLabel(value) || (enabled ? focus.label : undefined),
    studentCollabLevels: enabled
      ? resolveByReportName(collabRollups, nameById)
      : undefined,
    soloAccuracy: enabled ? soloAccuracy : undefined,
    endOfSessionSurvey: enabled ? endOfSessionSurvey : undefined,
    taggedQuotes,
    // Keyed by question number, which is the challenge's position in the list.
    submissionsByQuestion: enabled
      ? Object.fromEntries(challenges.map((c, i) => [i + 1, c?.submissions || []]))
      : undefined,
    questionLevels: enabled ? challengeData?.[value?.session_id] : undefined,
    fallbackQuestions: enabled
      ? new Set(
          challenges
            .map((c, i) => (c?.is_fallback ? i + 1 : null))
            .filter((n) => n !== null),
        )
      : undefined,
  };
};
